import {
    ExtractedFormPageData,
    ExtractedFormResult,
    Form1040PageData,
    Form1040NRPageData,
    Form1040SRPageData,
    Form1040XPageData,
    Form4562PageData,
    Form4868PageData,
    ScheduleCPageData,
    Schedule1PageData,
    ScheduleEPageData,
    ScheduleFPageData,
    ScheduleDPageData,
    ScheduleBPageData,
    Form4684PageData,
    Form8582PageData,
    Form1065PageData,
    ScheduleK1Form1065PageData,
    Form1120SPageData,
    ScheduleK1Form1120SPageData,
    Form8825PageData,
    Form1120PageData,
    Form1125EPageData,
    ScheduleM3Form1065PageData,
    ScheduleM3Form1120SPageData,
    ScheduleM3Form1120PageData,
    FormW2PageData,
    StatementsPageData,
} from "./extracted";
import { Loan, LoanBorrower, LoanBusiness } from "./loan";

export interface TaxReturnPage {
    form?: {
        type: string;
        page: number;
    };
    taxYear?: number;
    isOnList: boolean;
    isStateTaxReturn: boolean;
    identifiedForm?: string;
    pageNumber: number;
    confidence: number;
    reasoning: string;
}

export interface ReturnFile {
    name: string;
    sorted: boolean;
    processed: boolean;
}

export type { PipelineEnvelope, PipelineStage, BlobRef } from './pipeline-envelope.types';
import type { PipelineEnvelope } from './pipeline-envelope.types';

export interface GroupReturn {
    filePath: string;
    priority?: "low" | "medium" | "high";
    inUse: boolean;
    user?: string;
    message: PipelineEnvelope;
}

export interface IdentifyReturn {
    filePath: string;
    priority?: "low" | "medium" | "high";
    inUse: boolean;
    user?: string;
    message: PipelineEnvelope;
}

interface FormError {
    formType: string;
    error: string;
}

export interface ExtractReturn {
    formErrors: FormError[];
    message: PipelineEnvelope;
    priority?: "low" | "medium" | "high";
    inUse: boolean;
    user?: string;
}

export interface ReturnReviews {
    fiCode: string;
    institutionCode: string;
    institutionName: string;
    loanNumber: string;
    priority?: "low" | "medium" | "high";
    inUse: boolean;
    user?: string;
    files: ReturnFile[];
}
export interface GroupPages {
    key: string;
    type: 'group';
    task: GroupReturn;
}

export interface ExtractPages {
    key: string;
    type: 'extract';
    task: ExtractReturn;
}

export interface IdentifyPages {
    key: string;
    type: 'identify';
    task: IdentifyReturn;
}

export type ReturnTask = IdentifyPages | ExtractPages | GroupPages;

export const TaxReturnFormTypeLabels = {
    1040: '1040',
    '1040-SR': '1040-SR',
    '1040-NR': '1040-NR',
    '1040-X': '1040-X',
    'Schedule 1': 'Schedule 1',
    'Schedule B': 'Schedule B',
    'Schedule C': 'Schedule C',
    'Schedule D': 'Schedule D',
    'Schedule E': 'Schedule E',
    'Schedule F': 'Schedule F',
    4562: '4562',
    4684: '4684',
    4868: '4868',
    'Schedule M-3 (1065)': 'Schedule M-3 (1065)',
    'Schedule M-3 (1120S)': 'Schedule M-3 (1120S)',
    'Schedule M-3 (1120)': 'Schedule M-3 (1120)',
    8825: '8825',
    8582: '8582',
    1120: '1120',
    '1125-E': '1125-E',
    'Schedule K-1 (1065)': 'Schedule K-1 (1065)',
    'Schedule K-1 (1120S)': 'Schedule K-1 (1120S)',
    1065: '1065',
    '1120S': '1120S',
    'W-2': 'W-2',
    Statements: 'Statements',
} as const;

export type TaxReturnFormType = keyof typeof TaxReturnFormTypeLabels;

export interface PdfBlobReference {
    path: string;
    size: number;
    container: string;
}

export interface IdentifyTask extends IdentifyPages {
    blobs: {
        pdfRef: PdfBlobReference;
        json: TaxReturnPage[];
        loan: Loan;
    };
}

export interface FormExtraction<T = ExtractedFormPageData> {
    extraction?: ExtractedFormResult<T>[];
    error?: string;
}

export interface FormExtractions {
    '1040'?: FormExtraction<Form1040PageData>;
    '1040-nr'?: FormExtraction<Form1040NRPageData>;
    '1040-sr'?: FormExtraction<Form1040SRPageData>;
    '1040-x'?: FormExtraction<Form1040XPageData>;
    '4562'?: FormExtraction<Form4562PageData>;
    '4868'?: FormExtraction<Form4868PageData>;
    'schedule_c'?: FormExtraction<ScheduleCPageData>;
    'schedule_1'?: FormExtraction<Schedule1PageData>;
    "schedule_e"?: FormExtraction<ScheduleEPageData>;
    "schedule_f"?: FormExtraction<ScheduleFPageData>;
    "schedule_d"?: FormExtraction<ScheduleDPageData>;
    "schedule_b"?: FormExtraction<ScheduleBPageData>;
    "4684"?: FormExtraction<Form4684PageData>;
    "8582"?: FormExtraction<Form8582PageData>;
    "1065"?: FormExtraction<Form1065PageData>;
    "schedule_k-1_1065"?: FormExtraction<ScheduleK1Form1065PageData>;
    "1120s"?: FormExtraction<Form1120SPageData>;
    "schedule_k-1_1120s"?: FormExtraction<ScheduleK1Form1120SPageData>;
    "8825"?: FormExtraction<Form8825PageData>;
    "1120"?: FormExtraction<Form1120PageData>;
    "1125-e"?: FormExtraction<Form1125EPageData>;
    "schedule_m-3_1065"?: FormExtraction<ScheduleM3Form1065PageData>;
    "schedule_m-3_1120s"?: FormExtraction<ScheduleM3Form1120SPageData>;
    "schedule_m-3_1120"?: FormExtraction<ScheduleM3Form1120PageData>;
    "w-2"?: FormExtraction<FormW2PageData>;
    statements?: FormExtraction<StatementsPageData>
}

interface TaxReturnItem {
    taxYear: string;
    tin: string;
    name?: string;
    formType: keyof FormExtractions;
    pages: {
        version: string;
        type: string;
        pageNumber: number;
        formPageNumber: number;
        overallConfidence: number;
        requiresManualReview: string;
        extractedData: {
            page: number;
            formPageNumber: number;
            taxYear: number;
            data: any;
            originalPage: number;
        };
    }[];
}

export interface GroupTask extends GroupPages {
    blobs: {
        pdfRef: PdfBlobReference;
        json: {
            [taxYear: string]: TaxReturnItem[]
        };
        loan: Loan;
    };
}

export interface IdentifyResult extends Omit<TaxReturnPage, 'taxYear'> {
    taxYear: number;
}

export interface ExtractTask extends ExtractPages {
    blobs: {
        pdfRef: PdfBlobReference;
        json: IdentifyResult[];
        extractedJson: FormExtractions;
        draftExtractedJson?: FormExtractions;
        loan: Loan;
    };
}

export type ReturnActive = IdentifyPages | ExtractPages | GroupPages;

export interface KeyLabel {
    key: string;
    label: string;
    forms?: string[];
    iiis?: string[];
}

export interface IBox {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    color: { r: number; g: number; b: number; };
    iii?: string;
    iiis?: string[];
    form?: string;
    fieldName?: KeyLabel;
    value?: string;
}

export interface Boxes { [id: number]: IBox; }

export interface PageLocation {
    x: number;
    y: number;
    h: number;
    w: number;
}

export interface Page {
    formName: string;
    pageNumber: number;
    iiis: string[];
    taxYear?: string;
    boxes: Boxes;
    formNameBox?: IBox;
    yearBox?: IBox;
    associatedPage?: string;
    iiiBoxes: Boxes;
    pageLocation: PageLocation;
}

export interface Pages { [pageNumber: number]: Page }

export interface UploadedReturn {
    name: string;
    url: string;
}

export interface Task {
    id: string;
    title: string;
    status: "pending" | "in_progress" | "in_use" | "completed";
    priority?: "low" | "medium" | "high";
    assignee?: string;
    key?: string;
    fileName?: string;
}

export interface LoanReturn {
    success: true;
    loanNumber: string;
    fiCode: string;
    borrowers: LoanBorrower[];
    businesses: LoanBusiness[];
    files: ReturnFile[];
}

export interface SortReturnBody {
    index: number;
    file: ReturnFile;
    loanNumber: string;
    fiCode: string;
    finalOrder: number[];
    deletions: number[];
    movements: Array<[number, number]>;
    rotations: Array<[number, number]>;
}

export interface BackupExtractJson {
    '1040'?: IdentifyResult[]
    '1040-nr'?: IdentifyResult[]
    '1040-sr'?: IdentifyResult[]
    '1040-x'?: IdentifyResult[]
    '4562'?: IdentifyResult[]
    '4868'?: IdentifyResult[]
    'schedule_c'?: IdentifyResult[]
    'schedule_1'?: IdentifyResult[]
    "schedule_e"?: IdentifyResult[]
    "schedule_f"?: IdentifyResult[]
    "schedule_d"?: IdentifyResult[]
    "schedule_b"?: IdentifyResult[]
    "4684"?: IdentifyResult[]
    "8582"?: IdentifyResult[]
    "1065"?: IdentifyResult[]
    "schedule_k-1_1065"?: IdentifyResult[]
    "1120s"?: IdentifyResult[]
    "schedule_k-1_1120s"?: IdentifyResult[]
    "8825"?: IdentifyResult[]
    "1120"?: IdentifyResult[]
    "1125-e"?: IdentifyResult[]
    "schedule_m-3_1065"?: IdentifyResult[]
    "schedule_m-3_1120s"?: IdentifyResult[]
    "schedule_m-3_1120"?: IdentifyResult[]
    "w-2"?: IdentifyResult[]
    statements?: IdentifyResult[]
}

// Form matching interfaces - Two-step workflow
export interface PageInfo {
    page: number;
    statementId?: string; // Only present for statements
    title?: string; // Statement title, only present for statements
    w2Id?: string; // Only present for form_w2
    casualtyShellId?: string; // 4684 form-level "shell" chip (header + totals + Sections C/D)
    casualtyPropertyId?: string; // 4684 individual property column chip
    casualtyLabel?: string; // 4684 chip label (e.g., "Section A · Col B · Lake house")
}

export interface PageGrouping {
    pages: PageInfo[]; // PDF page info with optional statement IDs
    formType: string; // e.g., "1040", "schedule_c"
    taxYear: string;
    formInstanceId: string; // Unique ID for this form instance
    tin?: string; // Normalized TIN from backend extraction
    name?: string; // Name on form from backend extraction
}

export interface FormAssignment {
    formInstanceId: string;
    assignedTo?: {
        type: 'borrower' | 'business' | 'trust';
        irsIncomeId: string;
        name: string;
    };
    parentFormId?: string;
    childFormIds?: string[];
    manualOverride?: boolean;
}

export interface MatchFormsData {
    // Step 1: Page groupings (user can modify)
    pageGroups: Record<string, PageGrouping>;

    // Step 2: Form assignments
    formAssignments: Record<string, FormAssignment>;

    taxYearCorrections: Record<string, string>;
    currentStep: 1 | 2; // Track which step user is on
}

export const PARENT_FORM_TYPES = [
    'form1040',
    'form1065',
    'form1120',
    'form1120s',
    'form4868',
    'schedule_c',
    'schedule_f',
] as const;

// Forms that have restricted assignment rules
export interface FormAssignmentRule {
    canBeParent: boolean;
    canHaveParent: boolean;
    allowedEntityTypes: ('borrower' | 'business' | 'trust')[];
}

export const FORM_ASSIGNMENT_RULES: Record<string, FormAssignmentRule> = {
    // 1040 family - individual tax returns
    form1040: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },
    form_1040: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },
    'form1040-nr': {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },
    'form1040-sr': {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },
    'form1040-x': {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },
    form4868: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },

    // Business returns
    form1065: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['business'],
    },
    form1120: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['business'],
    },
    form1120s: {
        canBeParent: true,
        canHaveParent: false,
        allowedEntityTypes: ['business'],
    },
    'form1125-e': {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },

    // Individual schedules
    schedule_1: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },
    schedule_b: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },
    schedule_c: {
        canBeParent: true,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },
    schedule_d: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },
    schedule_e: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },
    schedule_f: {
        canBeParent: true,
        canHaveParent: true,
        allowedEntityTypes: ['borrower'],
    },

    // K-1 forms - individual or trust
    schedule_k1_1065: {
        canBeParent: false,
        canHaveParent: false,
        allowedEntityTypes: ['borrower', 'trust'],
    },
    schedule_k1_1120s: {
        canBeParent: false,
        canHaveParent: false,
        allowedEntityTypes: ['borrower', 'trust'],
    },

    // M-3 schedules - business
    'schedule_m-3_1065': {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },
    'schedule_m-3_1120s': {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },
    'schedule_m-3_1120': {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },
    schedule_m3_1065: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },
    schedule_m3_1120s: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },
    schedule_m3_1120: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['business'],
    },

    // Multi-entity forms that can be children of parent forms
    form4562: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower', 'business', 'trust'],
    },
    form4684: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower', 'business', 'trust'],
    },
    form8582: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower', 'business', 'trust'],
    },
    form8825: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower', 'business', 'trust'],
    },

    form_1125e: {
        canBeParent: false,
        canHaveParent: false,
        allowedEntityTypes: ['business'],
    },

    // W-2 - individual only
    'w-2': {
        canBeParent: false,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },

    form_w2: {
        canBeParent: false,
        canHaveParent: false,
        allowedEntityTypes: ['borrower'],
    },

    // Statements - all entity types, can be children
    statements: {
        canBeParent: false,
        canHaveParent: true,
        allowedEntityTypes: ['borrower', 'business', 'trust'],
    },
};
